import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Lock, Search, CheckCircle, AlertTriangle, Mail } from 'lucide-react';

const Home = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Image with Opacity */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/images/manitoba-hero-bg.jpg)',
            opacity: 0.65
          }}
        ></div>
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/30 to-blue-600/20"></div>
        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
              {language === 'en' ? (
                <span>
                  <span className="text-yellow-300 text-5xl md:text-6xl">C</span>ommunity{' '}
                  <span className="text-yellow-300 text-5xl md:text-6xl">A</span>ssisted{' '}
                  <span className="text-yellow-300 text-5xl md:text-6xl">P</span>olicing{' '}
                  <span className="text-yellow-300 text-5xl md:text-6xl">T</span>hrough{' '}
                  <span className="text-yellow-300 text-5xl md:text-6xl">U</span>se of{' '}
                  <span className="text-yellow-300 text-5xl md:text-6xl">R</span>ecorded{' '}
                  <span className="text-yellow-300 text-5xl md:text-6xl">E</span>vidence
                </span>
              ) : (
                <span>
                  <span className="text-yellow-300 text-5xl md:text-6xl">C</span>ommunaute{' '}
                  <span className="text-yellow-300 text-5xl md:text-6xl">A</span>ssistant la{' '}
                  <span className="text-yellow-300 text-5xl md:text-6xl">P</span>olice par{' '}
                  l'u<span className="text-yellow-300 text-5xl md:text-6xl">T</span>ilisation de{' '}
                  pre<span className="text-yellow-300 text-5xl md:text-6xl">U</span>ves{' '}
                  en<span className="text-yellow-300 text-5xl md:text-6xl">R</span>egistrees{' '}
                  <span className="text-yellow-300 text-5xl md:text-6xl">E</span>lectroniquement
                </span>
              )}
            </h1>
            <p className="text-xl text-white mb-4 drop-shadow-md">
              {t.heroSubtitle}
            </p>
            <p className="text-lg text-white/95 mb-8 max-w-3xl mx-auto drop-shadow-md">
              {t.heroDescription}
            </p>
            <Button 
              size="lg" 
              onClick={() => navigate('/register')}
              className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 font-semibold"
            >
              {t.registerNow}
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            {t.whyRegister}
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="border-2 border-blue-100 hover:border-blue-300 transition-all duration-300 hover:shadow-lg">
              <CardContent className="pt-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <Lock className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {t.secureTitle}
                </h3>
                <p className="text-gray-600">
                  {t.secureDesc}
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-100 hover:border-blue-300 transition-all duration-300 hover:shadow-lg">
              <CardContent className="pt-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <Search className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {t.solveCrimeTitle}
                </h3>
                <p className="text-gray-600">
                  {t.solveCrimeDesc}
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-100 hover:border-blue-300 transition-all duration-300 hover:shadow-lg">
              <CardContent className="pt-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <CheckCircle className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {t.freeEasyTitle}
                </h3>
                <p className="text-gray-600">
                  {t.freeEasyDesc}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Emergency / crime-reporting notice */}
      <section className="py-6 bg-red-50 border-y border-red-200" aria-labelledby="emergency-notice-heading">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex gap-3 items-start">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <h2 id="emergency-notice-heading" className="text-lg font-semibold text-red-900 mb-1">
                {t.emergencyNoticeTitle}
              </h2>
              <p className="text-sm text-red-900 leading-relaxed">
                {t.emergencyNotice}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Registration CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t.registrationEasy}
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            {t.registrationDesc}
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
          >
            {t.registerNow}
          </Button>
        </div>
      </section>

      {/* Questions Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t.questions}
          </h2>
          <p className="text-lg text-gray-600 mb-6">
            {t.questionsDesc}
          </p>
          <p className="text-lg text-gray-700 mb-8">
            {t.checkFaq}
          </p>

          {/* Program support contact */}
          <div className="mt-10 max-w-2xl mx-auto bg-blue-50 border border-blue-200 rounded-lg p-6 text-left" aria-labelledby="support-heading">
            <div className="flex gap-3 items-start">
              <Mail className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <h2 id="support-heading" className="text-lg font-semibold text-gray-900 mb-2">
                  {t.supportHeading}
                </h2>
                <p className="text-gray-700 mb-3">
                  {t.supportPrompt}
                </p>
                <p className="mb-3">
                  <span className="text-gray-700">{t.supportEmailLabel}:{' '}</span>
                  <a
                    href="mailto:MB.RCMP.CAPTURE@rcmp-grc.gc.ca"
                    className="text-blue-700 underline underline-offset-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-50 break-all"
                  >
                    MB.RCMP.CAPTURE@rcmp-grc.gc.ca
                  </a>
                </p>
                <p className="text-sm text-gray-600">
                  {t.supportEmailDoNotReport}
                </p>
              </div>
            </div>
          </div>

          <Button 
            variant="outline"
            size="lg"
            onClick={() => navigate('/faq')}
            className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg"
          >
            {t.faq}
          </Button>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img 
                src="https://images.unsplash.com/photo-1656469940208-bcf04f46c352?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxNYW5pdG9iYSUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3NjU4NTgxNTF8MA&ixlib=rb-4.1.0&q=85"
                alt="Manitoba Landscape"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img 
                src="/images/rural-community.jpg"
                alt="Rural Community"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;